<template>
	<div class="gallery-slider">
		<div class="gallery-slider__track" ref="trackRef">
			<div
				v-for="(image, i) in images"
				:key="i"
				class="gallery-slider__slide"
				ref="slideRefs"
			>
				<img class="img-bg__banner" :src="image.src" :alt="image.alt">
			</div>
		</div>

		<template v-if="images.length > 1">
			<!-- desktop: invisible click zones over the left/right half of the image -->
			<div class="gallery-slider__zones">
				<button type="button" class="gallery-slider__zone" aria-label="Vorheriges Bild" @click="scroll('left')"></button>
				<button type="button" class="gallery-slider__zone" aria-label="Nächstes Bild" @click="scroll('right')"></button>
			</div>

			<!-- mobile/tablet: visible tap arrow buttons -->
			<button
				type="button"
				class="button-icon__left button-icon-blue-white button-icon-hover-to-blue gallery-slider__arrow gallery-slider__arrow--prev"
				aria-label="Vorheriges Bild"
				@click="scroll('left')"
			></button>
			<button
				type="button"
				class="button-icon__right button-icon-blue-white button-icon-hover-to-blue gallery-slider__arrow gallery-slider__arrow--next"
				aria-label="Nächstes Bild"
				@click="scroll('right')"
			></button>
		</template>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";

const props = defineProps<{
  images: { src: string; alt: string }[];
}>();

const trackRef = ref<HTMLElement | null>(null);
const slideRefs = ref<HTMLElement[]>([]);
const currentIndex = ref(0);

function getTargetScroll(index: number): number | null {
  const track = trackRef.value;
  const slide = slideRefs.value[0];
  if (!track || !slide) return null;

  const containerWidth = track.getBoundingClientRect().width;
  const slideWidth = slide.getBoundingClientRect().width;
  const gap = Number.parseFloat(getComputedStyle(track).columnGap || "0") || 0;
  const paddingLeft = Number.parseFloat(getComputedStyle(track).paddingLeft || "0") || 0;

  return index * (slideWidth + gap) - (containerWidth - slideWidth) / 2 + paddingLeft;
}

function scroll(direction: "left" | "right") {
  const max = props.images.length - 1;
  currentIndex.value = direction === "right"
    ? Math.min(max, currentIndex.value + 1)
    : Math.max(0, currentIndex.value - 1);

  const target = getTargetScroll(currentIndex.value);
  if (target !== null && trackRef.value) {
    trackRef.value.scrollTo({ left: target, behavior: "smooth" });
  }
}

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  if (!trackRef.value) return;
  resizeObserver = new ResizeObserver(() => {
    const target = getTargetScroll(currentIndex.value);
    if (target !== null && trackRef.value) {
      trackRef.value.scrollLeft = target;
    }
  });
  resizeObserver.observe(trackRef.value);
});

onUnmounted(() => {
  resizeObserver?.disconnect();
});
</script>
